import { useLayoutEffect, useRef } from 'react'

const activeLocks = new Set()
let savedScrollY = 0
let savedStyles = null
let lockMode = null
let scrollBlockHandler = null
let lockIdSeq = 0

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 768px)').matches

export default function useBodyScrollLock(isLocked, options = {}) {
  const { allowSticky = false, allowScrollRef = null } = options
  const idRef = useRef(null)
  if (idRef.current === null) {
    lockIdSeq += 1
    idRef.current = lockIdSeq
  }

  useLayoutEffect(() => {
    if (!isLocked) return

    const id = idRef.current
    const body = document.body
    const html = document.documentElement

    const wasEmpty = activeLocks.size === 0
    activeLocks.add(id)

    if (wasEmpty) {
      lockMode = allowSticky
        ? 'listener'
        : isMobileViewport()
          ? 'fixed'
          : 'overflow'

      savedStyles = {
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        bodyPaddingRight: body.style.paddingRight,
        bodyTouchAction: body.style.touchAction,
        htmlOverflow: html.style.overflow,
      }

      if (lockMode === 'fixed') {
        savedScrollY = window.scrollY || window.pageYOffset
        body.style.overflow = 'hidden'
        body.style.touchAction = 'none'
        html.style.overflow = 'hidden'
        body.style.position = 'fixed'
        body.style.top = `-${savedScrollY}px`
        body.style.width = '100%'
      } else if (lockMode === 'overflow') {
        const scrollbarWidth = window.innerWidth - html.clientWidth
        html.style.overflow = 'hidden'
        if (scrollbarWidth > 0) {
          const currentPadding = parseFloat(window.getComputedStyle(body).paddingRight) || 0
          body.style.paddingRight = `${currentPadding + scrollbarWidth}px`
        }
      } else {
        scrollBlockHandler = event => {
          const allowEl = allowScrollRef?.current
          if (allowEl) {
            const path = event.composedPath?.()
            if (path?.includes(allowEl) || allowEl.contains(event.target)) {
              return
            }
          }
          event.preventDefault()
        }

        document.addEventListener('touchmove', scrollBlockHandler, { passive: false })
        document.addEventListener('wheel', scrollBlockHandler, { passive: false })
      }
    }

    return () => {
      activeLocks.delete(id)

      if (activeLocks.size === 0 && savedStyles) {
        const {
          bodyOverflow,
          bodyPosition,
          bodyTop,
          bodyWidth,
          bodyPaddingRight,
          bodyTouchAction,
          htmlOverflow,
        } = savedStyles

        const htmlScrollBehavior = html.style.scrollBehavior
        if (lockMode === 'fixed') {
          html.style.scrollBehavior = 'auto'
        }

        body.style.overflow = bodyOverflow
        body.style.position = bodyPosition
        body.style.top = bodyTop
        body.style.width = bodyWidth
        body.style.paddingRight = bodyPaddingRight
        body.style.touchAction = bodyTouchAction
        html.style.overflow = htmlOverflow

        if (lockMode === 'fixed') {
          window.scrollTo(0, savedScrollY)
          html.style.scrollBehavior = htmlScrollBehavior
        } else if (lockMode === 'listener' && scrollBlockHandler) {
          document.removeEventListener('touchmove', scrollBlockHandler)
          document.removeEventListener('wheel', scrollBlockHandler)
          scrollBlockHandler = null
        }

        savedStyles = null
        lockMode = null
      }
    }
  }, [isLocked, allowSticky])
}
